import React, { useEffect, useMemo, useState } from 'react'

import {
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    Chip,
    TextField,
    Theme,
    Typography,
    makeStyles,
} from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import LocationOnIcon from '@material-ui/icons/LocationOn'
import SchoolIcon from '@material-ui/icons/School'
import { Autocomplete } from '@material-ui/lab'

import { EzosLoader } from '../../../../components'
import { SchoolListProvider, useSchoolList } from '../../../../contexts'
import { useServedSchools } from '../../../../hooks/useServedSchools'
import { School, schoolDisplayName } from '../../../../models'
import {
    ServedSchoolPatchItem,
    ServedSchoolRow,
} from '../../../../models/Company/types'
import {
    filterSchoolAutocompleteOptions,
    formatSchoolLocation,
    schoolOptionChipLabel,
} from '../../../../utils/schoolSearch'

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
    },
    pageHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(2),
        flexWrap: 'wrap',
    },
    pageTitle: {
        fontWeight: 700,
        fontSize: '1.75rem',
        lineHeight: '37px',
        color: '#2F2E41',
    },
    caption: {
        color: theme.palette.text.secondary,
        fontSize: '0.8125rem',
        lineHeight: 1.4,
    },
    schoolGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
        gap: theme.spacing(1.5),
        alignContent: 'start',
    },
    schoolCard: {
        cursor: 'pointer',
        transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
        '&:hover': {
            boxShadow: theme.shadows[2],
        },
    },
    schoolCardServed: {
        borderColor: theme.palette.primary.main,
        backgroundColor: theme.palette.primary.main + '08',
    },
    schoolCardPrimary: {
        borderWidth: 2,
        borderColor: theme.palette.primary.main,
    },
    schoolCardDisabled: {
        opacity: 0.6,
        pointerEvents: 'none',
    },
    cardContent: {
        padding: theme.spacing(1.5),
        '&:last-child': {
            paddingBottom: theme.spacing(1.5),
        },
    },
    cardHeader: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: theme.spacing(1),
    },
    schoolIcon: {
        width: 36,
        height: 36,
        borderRadius: theme.spacing(1),
        backgroundColor: theme.palette.primary.main + '14',
        color: theme.palette.primary.main,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        marginTop: 2,
    },
    cardTitleRow: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: theme.spacing(0.5),
        minWidth: 0,
    },
    schoolName: {
        fontWeight: 600,
        fontSize: '0.875rem',
        lineHeight: 1.3,
    },
    metaList: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(0.25),
        marginTop: theme.spacing(1),
    },
    metaRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
    },
    cardFooter: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginTop: theme.spacing(1.25),
    },
    primaryChip: {
        height: 22,
        fontSize: '0.6875rem',
    },
    addSchoolCard: {
        borderStyle: 'dashed',
        borderColor: theme.palette.divider,
        backgroundColor: theme.palette.action.hover,
        cursor: 'pointer',
        transition: 'border-color 0.15s ease, background-color 0.15s ease',
        '&:hover': {
            borderColor: theme.palette.primary.main,
            backgroundColor: theme.palette.primary.main + '08',
        },
    },
    addSchoolCardActive: {
        borderStyle: 'solid',
        cursor: 'default',
        backgroundColor: theme.palette.background.paper,
        '&:hover': {
            borderColor: theme.palette.divider,
            backgroundColor: theme.palette.background.paper,
        },
    },
    addSchoolCardContent: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 120,
        padding: theme.spacing(1.5),
        '&:last-child': {
            paddingBottom: theme.spacing(1.5),
        },
    },
    addSchoolField: {
        width: '100%',
    },
}))

interface SchoolsServedSettingsInnerProps {
    apartmentId: number
}

const formatLocation = (row: ServedSchoolRow) => {
    if (row.city && row.state) {
        return `${row.city}, ${row.state}`
    }
    return row.city ?? row.state ?? null
}

const toPatchPayload = (rows: ServedSchoolRow[]): ServedSchoolPatchItem[] =>
    rows.map((row) => ({
        school_id: row.school_id,
        is_served: row.is_served,
        is_primary: row.is_primary,
    }))

const SchoolsServedSettingsInner: React.FC<SchoolsServedSettingsInnerProps> = ({
    apartmentId,
}) => {
    const classes = useStyles()
    const {
        schoolList,
        getSchoolList,
        loading: schoolsLoading,
    } = useSchoolList()
    const {
        servedSchools,
        isLoading,
        isSaving,
        saveServedSchools,
        addServedSchool,
    } = useServedSchools(apartmentId)

    const [draft, setDraft] = useState<ServedSchoolRow[]>([])
    const [addSchool, setAddSchool] = useState<School | null>(null)
    const [isAddingSchool, setIsAddingSchool] = useState(false)

    useEffect(() => {
        void getSchoolList({
            apartment_id: apartmentId,
            outreach_eligible_only: true,
        })
    }, [apartmentId, getSchoolList])

    useEffect(() => {
        if (servedSchools?.schools) {
            setDraft(servedSchools.schools)
        }
    }, [servedSchools])

    const existingIds = useMemo(
        () => new Set(draft.map((row) => row.school_id)),
        [draft],
    )

    const addableSchools = useMemo(
        () => schoolList.filter((s) => !existingIds.has(s.id)),
        [schoolList, existingIds],
    )

    const persistDraft = async (nextDraft: ServedSchoolRow[]) => {
        const previousDraft = draft
        setDraft(nextDraft)
        try {
            await saveServedSchools(toPatchPayload(nextDraft))
        } catch {
            setDraft(previousDraft)
        }
    }

    const toggleServed = async (schoolId: number, checked: boolean) => {
        if (isSaving) return

        const nextDraft = draft.map((row) => {
            if (row.school_id !== schoolId) return row
            return {
                ...row,
                is_served: checked,
                is_primary: checked ? row.is_primary : false,
            }
        })

        await persistDraft(nextDraft)
    }

    const handlePrimaryChange = async (schoolId: number) => {
        if (isSaving) return

        const nextDraft = draft.map((row) => ({
            ...row,
            is_primary: row.school_id === schoolId,
            is_served: row.school_id === schoolId ? true : row.is_served,
        }))

        await persistDraft(nextDraft)
    }

    const handleAddSchoolSelect = async (school: School | null) => {
        if (!school || isSaving) {
            setAddSchool(null)
            setIsAddingSchool(false)
            return
        }

        setAddSchool(null)
        setIsAddingSchool(false)
        try {
            await addServedSchool(school.id)
        } catch {
            // Error toast handled in hook
        }
    }

    const handleCloseAddSchool = () => {
        setAddSchool(null)
        setIsAddingSchool(false)
    }

    const loading = isLoading || schoolsLoading
    const inferenceRadius = servedSchools?.inference_radius_miles ?? 20
    const defaultRadius = servedSchools?.default_served_radius_miles ?? 5

    return (
        <Box className={classes.root}>
            <Box className={classes.pageHeader}>
                <Typography className={classes.pageTitle}>
                    Schools Served
                </Typography>
            </Box>

            <Typography className={classes.caption}>
                Schools within {inferenceRadius} mi are listed; those within{' '}
                {defaultRadius} mi are selected by default. Changes save
                automatically.
            </Typography>

            {loading ? (
                <EzosLoader />
            ) : (
                <Box className={classes.schoolGrid}>
                    {draft.map((row) => {
                        const location = formatLocation(row)
                        const cardClassName = [
                            classes.schoolCard,
                            row.is_served ? classes.schoolCardServed : '',
                            row.is_primary ? classes.schoolCardPrimary : '',
                            isSaving ? classes.schoolCardDisabled : '',
                        ]
                            .filter(Boolean)
                            .join(' ')

                        return (
                            <Card
                                key={row.school_id}
                                variant="outlined"
                                className={cardClassName}
                                onClick={() =>
                                    void toggleServed(
                                        row.school_id,
                                        !row.is_served,
                                    )
                                }
                            >
                                <CardContent className={classes.cardContent}>
                                    <Box className={classes.cardHeader}>
                                        <Box className={classes.schoolIcon}>
                                            <SchoolIcon fontSize="small" />
                                        </Box>
                                        <Box flex={1} minWidth={0}>
                                            <Box
                                                className={classes.cardTitleRow}
                                            >
                                                <Typography
                                                    className={
                                                        classes.schoolName
                                                    }
                                                >
                                                    {row.effective_display_name}
                                                </Typography>
                                                {row.is_primary && (
                                                    <Chip
                                                        size="small"
                                                        label="Primary"
                                                        color="primary"
                                                        className={
                                                            classes.primaryChip
                                                        }
                                                    />
                                                )}
                                            </Box>
                                            <Box className={classes.metaList}>
                                                {location && (
                                                    <Box
                                                        className={
                                                            classes.metaRow
                                                        }
                                                    >
                                                        <LocationOnIcon
                                                            style={{
                                                                fontSize: 14,
                                                            }}
                                                        />
                                                        <span>{location}</span>
                                                    </Box>
                                                )}
                                                {row.inferred_distance_miles !=
                                                    null && (
                                                    <Box
                                                        className={
                                                            classes.metaRow
                                                        }
                                                    >
                                                        <span>
                                                            {row.inferred_distance_miles.toFixed(
                                                                1,
                                                            )}{' '}
                                                            mi away
                                                        </span>
                                                    </Box>
                                                )}
                                                {row.fall_enrollment_total !=
                                                    null && (
                                                    <Box
                                                        className={
                                                            classes.metaRow
                                                        }
                                                    >
                                                        <span>
                                                            {row.fall_enrollment_total.toLocaleString()}{' '}
                                                            students
                                                        </span>
                                                    </Box>
                                                )}
                                                {row.source === 'manual' && (
                                                    <Box
                                                        className={
                                                            classes.metaRow
                                                        }
                                                    >
                                                        <span>
                                                            Added manually
                                                        </span>
                                                    </Box>
                                                )}
                                            </Box>
                                        </Box>
                                        <Checkbox
                                            size="small"
                                            color="primary"
                                            checked={row.is_served}
                                            disabled={isSaving}
                                            onClick={(e) => e.stopPropagation()}
                                            onChange={(e) =>
                                                void toggleServed(
                                                    row.school_id,
                                                    e.target.checked,
                                                )
                                            }
                                        />
                                    </Box>
                                    {row.is_served && !row.is_primary && (
                                        <Box className={classes.cardFooter}>
                                            <Chip
                                                size="small"
                                                label="Set as primary"
                                                variant="outlined"
                                                className={classes.primaryChip}
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    void handlePrimaryChange(
                                                        row.school_id,
                                                    )
                                                }}
                                            />
                                        </Box>
                                    )}
                                </CardContent>
                            </Card>
                        )
                    })}
                    <Card
                        variant="outlined"
                        className={`${classes.addSchoolCard} ${
                            isAddingSchool ? classes.addSchoolCardActive : ''
                        }`}
                        onClick={
                            isAddingSchool
                                ? undefined
                                : () => setIsAddingSchool(true)
                        }
                    >
                        <CardContent className={classes.addSchoolCardContent}>
                            {isAddingSchool ? (
                                <Autocomplete
                                    className={classes.addSchoolField}
                                    open
                                    options={addableSchools}
                                    value={addSchool}
                                    disabled={isSaving}
                                    loading={schoolsLoading}
                                    filterOptions={(options, { inputValue }) =>
                                        filterSchoolAutocompleteOptions(
                                            options,
                                            inputValue,
                                        )
                                    }
                                    onClose={(_e, reason) => {
                                        if (
                                            reason === 'escape' ||
                                            reason === 'blur'
                                        ) {
                                            handleCloseAddSchool()
                                        }
                                    }}
                                    onChange={(_e, value) =>
                                        void handleAddSchoolSelect(value)
                                    }
                                    getOptionLabel={(option) =>
                                        schoolOptionChipLabel(option)
                                    }
                                    renderOption={(option) => {
                                        const location = formatSchoolLocation(
                                            option,
                                        )
                                        return (
                                            <Box>
                                                <Typography variant="body2">
                                                    {schoolDisplayName(option)}
                                                </Typography>
                                                {location ? (
                                                    <Typography
                                                        variant="caption"
                                                        color="textSecondary"
                                                        component="div"
                                                    >
                                                        {location}
                                                    </Typography>
                                                ) : null}
                                            </Box>
                                        )
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            autoFocus
                                            variant="outlined"
                                            label="Add school"
                                            placeholder="Search by name, city, or acronym (e.g. LSU)..."
                                            size="small"
                                        />
                                    )}
                                    noOptionsText="No schools found"
                                    loadingText="Loading schools..."
                                />
                            ) : (
                                <Button
                                    color="primary"
                                    startIcon={<AddIcon />}
                                    disabled={
                                        isSaving || addableSchools.length === 0
                                    }
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        setIsAddingSchool(true)
                                    }}
                                >
                                    Add school
                                </Button>
                            )}
                        </CardContent>
                    </Card>
                </Box>
            )}
        </Box>
    )
}

interface SchoolsServedSettingsProps {
    apartmentId: number | undefined
}

export const SchoolsServedSettings: React.FC<SchoolsServedSettingsProps> = ({
    apartmentId,
}) => {
    if (apartmentId == null) {
        return null
    }
    return (
        <SchoolListProvider>
            <SchoolsServedSettingsInner apartmentId={apartmentId} />
        </SchoolListProvider>
    )
}
